import { createData } from "../../../util/API";
import { uploadDirectToS3 } from "../../../util/S3Functions";

export const VENDOR_UPLOAD_FINISH_ENDPOINT = "/partner/vendor-upload-finish/";

export const getStorePhotoFiles = (storePhotos) =>
  (storePhotos || [])
    .map((item) => item.originFileObj || item)
    .filter((file) => file instanceof File);

export const buildStorePhotosMeta = (files) =>
  files.map((file) => ({
    name: file.name,
    content_type: file.type || "application/octet-stream",
  }));

const parseStorePhotoDocIndex = (docName) => {
  const match = String(docName || "").match(/store_photos_(\d+)/);
  return match ? Number(match[1]) : null;
};

export const confirmVendorUploadFinish = async ({ tempId, imageKey, docName }) => {
  const res = await createData(VENDOR_UPLOAD_FINISH_ENDPOINT, {
    temp_id: tempId,
    image_key: imageKey,
    doc_name: docName,
  });
  const body = res?.data;

  if (!res || res.status >= 400 || body?.status === false) {
    throw new Error(body?.message || "Failed to confirm file upload.");
  }

  return body;
};

/**
 * Step 2 & 3: upload files to S3 using presigned POST, then confirm each upload.
 */
export const uploadPresignedStorePhotos = async (files, presignedUrls) => {
  if (!files?.length || !presignedUrls?.length) {
    return;
  }

  const orderedPresigned = [...presignedUrls].sort((a, b) => {
    const indexA = parseStorePhotoDocIndex(a.doc_name);
    const indexB = parseStorePhotoDocIndex(b.doc_name);
    return (indexA ?? 0) - (indexB ?? 0);
  });

  for (let i = 0; i < orderedPresigned.length; i += 1) {
    const presigned = orderedPresigned[i];
    const fileIndex = parseStorePhotoDocIndex(presigned.doc_name) ?? i;
    const file = files[fileIndex];

    if (!file) {
      throw new Error(`Missing file for ${presigned.doc_name || `upload ${i}`}.`);
    }

    if (!presigned.url || !presigned.fields) {
      throw new Error("Invalid presigned upload data from server.");
    }

    await uploadDirectToS3(file, {
      url: presigned.url,
      fields: presigned.fields,
    });

    const imageKey =
      presigned.file_key || presigned.fields?.key || presigned.image_key;

    await confirmVendorUploadFinish({
      tempId: presigned.temp_id,
      imageKey,
      docName: presigned.doc_name,
    });
  }
};

export const attachStorePhotosToPayload = (payload, storePhotos) => {
  const files = getStorePhotoFiles(storePhotos);
  if (!files.length) {
    return payload;
  }

  return {
    ...payload,
    lead_data: {
      ...payload.lead_data,
      store_photos: buildStorePhotosMeta(files),
    },
  };
};
